//precision highp float;

uniform float uColTemp;
uniform float uColTempMult;
//uniform float uTime;
// uniform float uSize;
uniform float uStrength;

varying vec2 vUv;
// varying vec4 vWorldPosition;

#define PI 3.1415926538

//
// Description : Array and textureless GLSL 2D/3D/4D simplex
//               noise functions.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : ijm
//     Lastmod : 20110822 (ijm)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//

vec3 mod289_2D(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec2 mod289_2D(vec2 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec3 permute(vec3 x) {
  return mod289_2D(((x * 34.0) + 1.0) * x);
}

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
  0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
  -0.577350269189626,  // -1.0 + 2.0 * C.x
  0.024390243902439); // 1.0 / 41.0
// First corner
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);

// Other corners
  vec2 i1;
  //i1.x = step( x0.y, x0.x ); // x0.x > x0.y ? 1.0 : 0.0
  //i1.y = 1.0 - i1.x;
  i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  // x0 = x0 - 0.0 + 0.0 * C.xx ;
  // x1 = x0 - i1 + 1.0 * C.xx ;
  // x2 = x0 - 1.0 + 2.0 * C.xx ;
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;

// Permutations
  i = mod289_2D(i); // Avoid truncation effects in permutation
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));

  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;

// Gradients: 41 points uniformly over a line, mapped onto a diamond.
// The ring size 17*17 = 289 is close to a multiple of 41 (41*7 = 287)

  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;

// Normalise gradients implicitly by scaling m
// Approximation of: m *= inversesqrt( a0*a0 + h*h );
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);

// Compute final noise value at P
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float fbm2d(vec2 x, const in int it) {
  float v = 0.0;
  float a = 0.5;
  vec2 shift = vec2(100);

  for(int i = 0; i < 32; ++i) {
    if(i < it) {
      v += a * snoise(x);
      x = x * 2.0 + shift;
      a *= 0.5;
    }
  }
  return v;
}

vec3 brightnessToColor(float b) {
  b *= uColTemp;
  return (vec3(b, b * b, b * b * b * b) / uColTemp * uColTempMult);
}

// THE ART OF CODE YOUTUBE

mat2 Rot(float a) {
  float s = sin(a), c = cos(a);
  return mat2(c, -s, s, c);
}

void main() {

  vec2 uv = vUv - vec2(0.5);

    // HERE WE ARE MAKING SOME SPHERICAL FRACTAL BROWNIAN NOISE 
    // AND APPLYING DOMAIN WARPING TO IT TO CREATE THE WAVY HAZY GLOW
    // SEE https://iquilezles.org/www/index.htm for more on those topics
  vec2 p = -1.0 + vUv * 2.0;
  p *= Rot(PI * 1.5);
  float r = length(p);
  float a = atan(p.y, p.x);
  a += 0.1 * fbm2d(vec2(3.0 * p), 3);
  float noise = fbm2d(vec2(3.0 * r, 8.0 * a), 3) + 1.0 * 0.5;

    // NOW LETS MAKE SOME GLOW THAT FALLS OFF TOWARDS THE EDGES
  float dist = length(uv);
  float glow = 0.05 / dist;

    // APPLY THE GLOW TO THE RADIAL NOISE WE MADE SO IT FADES OUT
  noise *= glow;

    // TIME FOR SOME STRONG RAYS FOR A LENS FLARE EFFECT
  float rays = max(0., 0.66 - abs(uv.x * uv.y * 4000.));

    // LETS MAKE SOME MORE BUT NOT AS STRONG AND ROTATE THEM
  uv *= Rot(PI / 1.124562);
  rays += max(0., 0.15 - abs(uv.x * uv.y * 1000.));

  uv *= Rot(PI / 2.283942);
  rays += max(0., 0.15 - abs(uv.x * uv.y * 1000.));

  uv *= Rot(PI / 0.348666);
  rays += max(0., 0.15 - abs(uv.x * uv.y * 1000.));

  float star = smoothstep(0.03, 0.02, dist);

    // TIME TO ADD EVERYTHING TOGETHER
  float brightness = star + rays + (noise * 0.6);

    // ADD SOME COLOUR WITH A FUNCTION THAT MIMICS COLOUR TEMPERATURE
    // WE CAN ADJUST THE PARAMS TO GET DIFFERENT COLOUR STARS
  vec3 col = vec3(brightness);//brightnessToColor(brightness);

    // THE EDGES OF THE PLANE WE ARE DRAWING ON ARE VISIBLE STILL SO WE
    // USE SMOOTHSTEP TO FADE TO OPACITY TO BLACK
  float edgeFade = smoothstep(0.55, 0.01, dist);

    // PRESS PRINT BRRRRRRRRRRR
    // gl_FragColor takes three colour values (stored in 'col') and an alpha value
    // the minimum value of brightness and our edgeFade 
  gl_FragColor = vec4(col, min(edgeFade, brightness));
}
